<template>
  <div ref="content" class="hero">
    <h1 ref="display" class="hero__display">
      <span>Get up to 6 months</span>
      <span>HALF PRICE*</span>
    </h1>
    <div ref="text" class="hero__content">
      <div class="hero__icons">
        <icon-hero-features />
      </div>
      <div v-if="withHyperCta" class="hero__cta">
        <ui-button tag="app-link" to="/hyperfibre">View Hyperfibre Plans Here</ui-button>
      </div>
    </div>
    <svg class="hero__svg" preserveAspectRatio="none" viewBox="0 0 731 353">
      <mask id="mask">
        <rect x="0" y="0" width="731" height="353" fill="white" />
        <path ref="leftBottom" d="M33.301 353C14.9093 353 0 337.993 0 319.482V353H33.301Z" fill="black" />
        <path ref="leftTop" d="M0 33.5181C0 15.0066 14.9094 0 33.3011 0H0V33.5181Z" fill="black" />
        <path ref="rightBottom" d="M731 322.996C731 339.565 717.569 353 701 353H731V322.996Z" fill="black" />
        <path
          ref="rightTop"
          d="M697.699 124.641C716.091 124.641 731 139.648 731 158.16V0H588.579C606.97 0 621.88 15.0066 621.88 33.5181V91.1233C621.88 109.635 636.789 124.641 655.181 124.641H697.699Z"
          fill="black"
        />
      </mask>
      <rect ref="block" x="0" y="0" width="731" height="353" fill="#E0C7E6" mask="url(#mask)" />
    </svg>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, useNuxtApp } from '#imports';
import IconHeroFeatures from '~/components/global/elements/IconHeroFeatures.vue';
import UiButton from '~/components/global/ui/UiButton.vue';

defineProps({
  withHyperCta: {
    type: Boolean,
    default: false,
  },
});

const content = ref(null);
const rightTop = ref(null);
const rightBottom = ref(null);
const leftTop = ref(null);
const leftBottom = ref(null);
const display = ref(null);
const text = ref(null);
const block = ref(null);

onMounted(() => {
  const { $gsap } = useNuxtApp();

  const tl = $gsap.timeline({ paused: true });

  $gsap.set(block.value, { scale: 0, opacity: 1, transformOrigin: '50% 50%' });
  $gsap.set(rightTop.value, { scale: 0.05, transformOrigin: '100% 0%' });
  $gsap.set(rightBottom.value, { scaleX: 0.05, transformOrigin: '100% 50%' });
  $gsap.set(leftTop.value, { scale: 0.05, transformOrigin: '0% 0%' });
  $gsap.set(leftBottom.value, { scale: 0.05, transformOrigin: '0% 100%' });
  $gsap.set(display.value, { y: 40, opacity: 0 });
  $gsap.set(text.value, { y: 40, opacity: 0 });
  $gsap.set(content.value, { opacity: 1 });

  tl.to(block.value, { scale: 1, opacity: 1, duration: 1, ease: 'power4.out' }, 0);
  tl.to(rightTop.value, { scale: 1, duration: 2, ease: 'power3.out' }, 0.1);
  tl.to(rightBottom.value, { scaleX: 1, duration: 2.5, ease: 'power3.out' }, 0.1);
  tl.to(leftTop.value, { scale: 1, duration: 4, ease: 'power4.out' }, 0.2);
  tl.to(leftBottom.value, { scale: 1, duration: 4, ease: 'power4.out' }, 0.2);
  tl.to(display.value, { y: 0, opacity: 1, duration: 1, ease: 'power3.out' }, 0.5);
  tl.to(text.value, { y: 0, opacity: 1, duration: 1, ease: 'power3.out' }, 0.8);

  setTimeout(() => {
    tl.play();
  }, 350);
});
</script>

<style lang="scss" scoped>
.hero {
  opacity: 0;
  @include fluid(width, 320, 520, 580);
  //@include fluid(width, 320, 640, 690);

  position: relative;
  z-index: 2;

  @include mobile {
    width: 100%;
    background-color: var(--dark-pink);
    border-radius: rem(20);
  }

  @include desktop {
    margin-left: rem(-40);
  }

  &__display {
    padding: rem(30) rem(30) 0;
    @include mobile {
      padding: rem(24) rem(24) 0;
      br {
        display: none;
      }
    }
    font-family: var(--heading-font-family);
    @include fluid(font-size, 32, 40, 48);
    line-height: 1.02;

    span {
      display: block;
    }
  }

  &__content {
    padding: rem(10) rem(30) rem(30);
    @include mobile {
      padding: rem(10) rem(24) rem(24);
    }
  }

  &__icons {
    width: 100%;
    max-width: rem(500);
    margin-top: 1em;
    :deep(svg) {
      display: block;
      width: 100%;
      height: auto;
    }
  }

  &__cta {
    margin-top: 1em;
  }

  &__text {
    font-weight: 500;
    line-height: 1.02;
    p + p {
      margin-top: 0.15em;
    }
    @include fluid(font-size, 22, 24, 32);
    small {
      font-size: 0.65em;
    }
  }

  &__svg {
    @include cover($z-index: -1);

    @include mobile {
      display: none;
    }

    * {
      transform-box: view-box;
    }
  }
}
</style>
